'use client';

import { type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useProfileQuery } from '@/lib/services/profile/hooks';
import { useLawFirmProfile } from '@/lib/services/account';
import {
  useStartTrial,
  useActivationSummary,
  type ActivationSummaryResponseDto
} from '@/lib/services/billing';
import { useFeatureFlags } from '@/lib/providers/feature-flags-provider';
import { ActivationV2Landing } from '@/components/trial-activation/activation-v2-landing';
import { ActivationV2Success } from '@/components/trial-activation/activation-v2-success';

const TRIAL_PARAM = 'trial';
const TRIAL_ACTIVATED_VALUE = 'activated';

/**
 * Pending firms see the activation gate by DEFAULT; this flag opts a pending
 * firm OUT (LF-4085). The deploy migration enables it for every firm already
 * `pending` at cutover, so the default-on gate only applies to firms that enter
 * `pending` afterwards.
 */
const TRIAL_GATE_OPT_OUT_FLAG = 'trial-gate-opt-out';

/**
 * active-free firms only see the gate when this flag opts them IN (LF-4086) —
 * the inverse of opt-out. It blocks the otherwise-active account until a card is
 * added, forcing the immediate-billing conversion the backend performs on
 * card-add.
 */
const TRIAL_GATE_OPT_IN_FLAG = 'trial-gate-opt-in';

/**
 * Membership statuses that mean the firm has CONVERTED after the Stripe
 * round-trip, so the gate may release: `trial` (the pending→trial path) or
 * `active` (the active-free→active immediate conversion). The pre-conversion
 * gated states (`pending`, `active-free`) are excluded — on return from Stripe
 * the gate polls until the backend webhook flips the firm to one of these
 * (LF-4535).
 */
const CONVERTED_STATUSES = ['trial', 'active'];

function isConverted(status?: string | null): boolean {
  return !!status && CONVERTED_STATUSES.includes(status);
}

/** How often to re-check the firm status while waiting for the Stripe webhook. */
const POLL_INTERVAL_MS = 2500;

function readReturningFromUrl(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    new URLSearchParams(window.location.search).get(TRIAL_PARAM) ===
    TRIAL_ACTIVATED_VALUE
  );
}

function FullScreenLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
    </div>
  );
}

/**
 * Owns the start-trial mutation and renders the data-driven activation landing.
 * The CTA drives a Stripe checkout session and redirects on success; the backend
 * branches the conversion on the firm's status (free trial for `pending`,
 * immediate no-trial conversion for `active-free`).
 */
function ActivationV2({
  userEmail,
  summary
}: {
  userEmail: string;
  summary: ActivationSummaryResponseDto;
}) {
  const startTrial = useStartTrial();

  const handleActivate = () => {
    const origin = window.location.origin;
    startTrial.mutate(
      {
        successUrl: `${origin}/?${TRIAL_PARAM}=${TRIAL_ACTIVATED_VALUE}`,
        cancelUrl: `${origin}/`
      },
      {
        onSuccess: (data) => {
          window.location.href = data.checkoutUrl;
        }
      }
    );
  };

  return (
    <ActivationV2Landing
      userEmail={userEmail}
      summary={summary}
      state={startTrial.isPending ? 'redirecting' : 'idle'}
      onActivate={handleActivate}
      errorMessage={
        startTrial.isError
          ? 'Something went wrong starting your trial. Please try again.'
          : undefined
      }
    />
  );
}

/**
 * Activation gate (LF-4086) — the permanent, data-driven activation page for all
 * firms. It blocks a firm with the "Welcome to Vale" activation summary until a
 * payment method is added. Applies when:
 *   • the firm is `pending` and has NOT opted out (`trial-gate-opt-out`), or
 *   • the firm is `active-free` and has opted IN (`trial-gate-opt-in`).
 * Every other firm renders the app untouched.
 *
 * On returning from Stripe (`?trial=activated`) the gate shows a temporary
 * success panel and polls the firm's `membershipStatus` until the backend
 * webhook flips it to `trial`/`active`, then hands to the app — restoring the
 * confirmation the v1 gate gave, which the v2 gate had dropped (LF-4535). The
 * poll also stops the gate re-firing: releasing to the app before the firm has
 * actually converted would re-show the gate.
 */
export function ActivationV2Gate({ children }: { children: ReactNode }) {
  const { data: profileData, isLoading: profileLoading } = useProfileQuery();
  const { hasFeature } = useFeatureFlags();

  const returning = readReturningFromUrl();

  // While returning from Stripe, poll until the webhook converts the firm.
  const { profile: firm, isLoading: firmLoading } = useLawFirmProfile({
    refetchInterval: (query) =>
      returning && !isConverted(query.state.data?.membershipStatus)
        ? POLL_INTERVAL_MS
        : false
  });

  const membershipStatus = firm?.membershipStatus;
  const converted = isConverted(membershipStatus);

  const gateApplies =
    (membershipStatus === 'pending' && !hasFeature(TRIAL_GATE_OPT_OUT_FLAG)) ||
    (membershipStatus === 'active-free' && hasFeature(TRIAL_GATE_OPT_IN_FLAG));

  // The summary backs the activation landing only; skip it on the return path.
  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError
  } = useActivationSummary({ enabled: gateApplies && !returning });

  // `active-free`/`active` → subscription framing (the active-free opt-in path
  // is an immediate paid conversion with no free trial); everything else,
  // including an unknown status, defaults to the less-presumptuous trial framing.
  const successVariant =
    membershipStatus === 'active-free' || membershipStatus === 'active'
      ? 'subscription'
      : 'trial';

  if (profileLoading || firmLoading) return <FullScreenLoader />;

  // Returning from Stripe — show the temporary success panel while the status
  // catches up (the poll above drives it), then hand to the app once converted.
  // Rendered after the load gate so `membershipStatus` is known on first paint
  // and the copy variant is correct (no brief pending→"subscription" flash).
  if (returning && !converted) {
    return (
      <ActivationV2Success
        firmName={firm?.name ?? profileData?.user?.lawFirm?.name ?? 'your firm'}
        userEmail={profileData?.user?.email ?? ''}
        variant={successVariant}
      />
    );
  }

  if (!gateApplies) return <>{children}</>;

  // Fail open if the summary can't be loaded — the gate is a FE-only commercial
  // prompt (the backend already grants pending / active-free firms full access),
  // so a transient fetch error should let the user into the app, not trap them
  // behind a spinner.
  if (summaryError) return <>{children}</>;
  if (summaryLoading || !summary) return <FullScreenLoader />;

  const userEmail = profileData?.user?.email ?? '';
  return <ActivationV2 userEmail={userEmail} summary={summary} />;
}
